'use client';

import * as React from 'react';
import { useRef, useCallback } from 'react';

const CODE_LENGTH = 6;

interface OtpInputProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    length?: number;
}

export function OtpInput({
    value,
    onChange,
    disabled,
    length = CODE_LENGTH,
}: OtpInputProps) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const focusInput = useCallback((index: number) => {
        const clamped = Math.max(0, Math.min(index, length - 1));
        inputRefs.current[clamped]?.focus();
    }, [length]);

    const handleChange = (index: number, char: string) => {
        if (!/^\d?$/.test(char)) return;
        const arr = value.padEnd(length, ' ').split('');
        arr[index] = char;
        const next = arr.join('').replace(/ /g, '');
        onChange(next.slice(0, length));
        if (char && index < length - 1) {
            focusInput(index + 1);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const arr = value.padEnd(length, ' ').split('');
            if (arr[index] !== ' ') {
                arr[index] = ' ';
                onChange(arr.join('').replace(/ /g, ''));
            } else if (index > 0) {
                arr[index - 1] = ' ';
                onChange(arr.join('').replace(/ /g, ''));
                focusInput(index - 1);
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusInput(index - 1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
        if (pasted) {
            onChange(pasted);
            focusInput(Math.min(pasted.length, length - 1));
        }
    };

    return (
        <div className="flex items-center justify-center gap-2">
            {Array.from({ length }).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={value[i] || ''}
                    disabled={disabled}
                    onChange={(e) => handleChange(i, e.target.value.slice(-1))}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    onFocus={(e) => e.target.select()}
                    className="
                        w-11 h-12 text-center text-xl font-semibold font-mono
                        rounded-lg border border-input bg-background
                        outline-none transition-all
                        focus:ring-2 focus:ring-ring focus:border-ring
                        disabled:opacity-50 disabled:cursor-not-allowed
                        placeholder:text-muted-foreground/30
                    "
                    placeholder="·"
                    autoComplete="one-time-code"
                />
            ))}
        </div>
    );
}
